import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import QuizReportModal from '@/components/QuizReportModal';
import ExamTakingModal from '@/components/ExamTakingModal';
import ExamReportModal from '@/components/ExamReportModal';
import QuestionPaperView from '@/components/student/QuestionPaperView';
import CounselorFeedbackList from '@/components/shared/CounselorFeedbackList';
import GamificationPanel from '@/components/gamification/GamificationPanel';
import CounselorTasks from '@/components/student/CounselorTasks';
import ProgressAnalytics from '@/components/student/ProgressAnalytics';
import NotificationBell from '@/components/shared/NotificationBell';
import { useFeedback } from '@/hooks/useFeedback';
import { FileText, CheckCircle, Send, Loader2, MessageSquare, ClipboardList } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quizSubject, setQuizSubject] = useState('mathematics');
  const [quizDifficulty, setQuizDifficulty] = useState('medium');
  const [numberOfQuestions, setNumberOfQuestions] = useState(5);
  const [selectedTimeLimit, setSelectedTimeLimit] = useState(300); // seconds
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizData, setQuizData] = useState(null); // Store full quiz data
  const [quizAnswers, setQuizAnswers] = useState({});
  const [progress, setProgress] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [mood, setMood] = useState('');
  const [energyLevel, setEnergyLevel] = useState(5);
  const [timeLimit, setTimeLimit] = useState(300); // seconds
  const [timeRemaining, setTimeRemaining] = useState(300);
  const [timerActive, setTimerActive] = useState(false);
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [quizReports, setQuizReports] = useState([]);
  const [userProfile, setUserProfile] = useState(null); // Store user profile with grade
  const chatEndRef = useRef(null);
  
  // Assignment and Exam state
  const [assignments, setAssignments] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [showExamModal, setShowExamModal] = useState(false);
  const [examSubmissionResult, setExamSubmissionResult] = useState(null);
  const [showExamReportModal, setShowExamReportModal] = useState(false);
  
  // Assignment submission state
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentFile, setAssignmentFile] = useState(null);
  const [assignmentNotes, setAssignmentNotes] = useState('');
  
  // Assignment view state
  const [showAssignmentViewDialog, setShowAssignmentViewDialog] = useState(false);
  const [viewingAssignment, setViewingAssignment] = useState(null);

  // Question paper answer state
  const [showQuestionPaperDialog, setShowQuestionPaperDialog] = useState(false);
  const [questionPaperAssignment, setQuestionPaperAssignment] = useState(null);
  const [paperAnswers, setPaperAnswers] = useState({});
  const [submittingPaper, setSubmittingPaper] = useState(false);
  const [paperResult, setPaperResult] = useState(null);
  const { feedback: counselorFeedback, unreadCount: feedbackUnread, markAsRead: markFeedbackRead } = useFeedback('student');
  const [studentMappings, setStudentMappings] = useState({ has_teacher: false, has_counselor: false, has_parent: false });

  useEffect(() => {
    fetchUserProfile();
    fetchProgress();
    fetchAchievements();
    fetchQuizReports();
    fetchAssignments();
    fetchExams();
    fetchStudentMappings();
  }, []);

  const fetchStudentMappings = async () => {
    try {
      const res = await axios.get(`${API}/student/my-mappings`);
      setStudentMappings(res.data);
    } catch { /* silent - default to no mappings */ }
  };

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(time => {
          if (time <= 1) {
            setTimerActive(false);
            handleSubmitQuiz(); // Auto-submit when time runs out
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (timeRemaining === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeRemaining]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress`);
      setProgress(response.data.progress || []);
    } catch (error) {
      console.error('Failed to fetch progress', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/student/dashboard`);
      if (response.data.profile) {
        setUserProfile(response.data.profile);
      }
    } catch (error) {
      console.error('Failed to fetch user profile', error);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`${API}/achievements`);
      setAchievements(response.data.achievements || []);
    } catch (error) {
      console.error('Failed to fetch achievements', error);
    }
  };

  const fetchQuizReports = async () => {
    try {
      const response = await axios.get(`${API}/quiz/reports`);
      setQuizReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch quiz reports', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API}/student/assignments`);
      setAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('Failed to fetch assignments', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/student/exams`);
      setExams(response.data.exams || []);
    } catch (error) {
      console.error('Failed to fetch exams', error);
    }
  };

  const handleStartExam = async (examId) => {
    try {
      const response = await axios.get(`${API}/student/exam/${examId}`);
      setSelectedExam(response.data);
      setShowExamModal(true);
    } catch (error) {
      toast.error('Failed to load exam');
    }
  };

  const handleExamSubmit = async (submissionData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/student/exam/submit`, submissionData);
      
      setExamSubmissionResult(response.data);
      setShowExamReportModal(true);
      toast.success('Exam submitted successfully!');
      fetchExams(); // Refresh exam list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const validTypes = [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a Word document (.doc or .docx)');
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setAssignmentFile(file);
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!selectedAssignment) return;
    
    if (!assignmentFile && !assignmentNotes.trim()) {
      toast.error('Please upload a file or add notes');
      return;
    }
    
    try {
      setLoading(true);
      
      let fileData = null;
      let fileName = null;
      let fileType = null;
      
      if (assignmentFile) {
        // Convert file to base64
        const reader = new FileReader();
        fileData = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(assignmentFile);
        });
        fileName = assignmentFile.name;
        fileType = assignmentFile.type;
      }
      
      const response = await axios.post(`${API}/student/assignment/submit`, null, {
        params: {
          assignment_id: selectedAssignment.id,
          content: assignmentNotes,
          file_data: fileData,
          file_name: fileName,
          file_type: fileType
        }
      });
      
      toast.success('Assignment submitted successfully!');
      setShowAssignmentModal(false);
      setSelectedAssignment(null);
      setAssignmentFile(null);
      setAssignmentNotes('');
      fetchAssignments(); // Refresh assignment list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleReportModalClose = () => {
    setShowReportModal(false);
    setCurrentQuiz(null);
    setQuizAnswers({});
    setTimeRemaining(0);
    fetchProgress();
    fetchAchievements();
    fetchQuizReports();
  };

  const handleQuestionPaperSubmit = async () => {
    if (!questionPaperAssignment) return;
    
    // Check at least one answer is filled
    const answeredCount = Object.values(paperAnswers).filter(a => 
      a !== '' && a !== null && a !== undefined && (!Array.isArray(a) || a.length > 0)
    ).length;
    
    if (answeredCount === 0) {
      toast.error('Please answer at least one question before submitting');
      return;
    }
    
    setSubmittingPaper(true);
    try {
      const response = await axios.post(`${API}/student/assignment/submit-answers`, {
        assignment_id: questionPaperAssignment.id,
        answers: paperAnswers
      });
      
      setPaperResult(response.data);
      toast.success(`Submitted! Score: ${response.data.earned_marks ?? 0}/${response.data.total_marks || 0} (${response.data.score_percentage ?? 0}%)`);
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answers');
    } finally {
      setSubmittingPaper(false);
    }
  };

  const openQuestionPaper = (assignment) => {
    setQuestionPaperAssignment(assignment);
    setPaperAnswers({});
    setPaperResult(null);
    setShowQuestionPaperDialog(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    const userMessage = message;
    setChatHistory([...chatHistory, { role: 'user', content: userMessage }]);
    setMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, { message: userMessage });
      setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuiz = async () => {
    setLoading(true);
    try {
      // Show informative message for larger question counts
      if (numberOfQuestions > 30) {
        toast.info(`Generating ${numberOfQuestions} questions... This may take 1-2 minutes. Large quizzes build up gradually with each generation.`);
      } else if (numberOfQuestions > 20) {
        toast.info('Generating quiz... This may take up to a minute for larger question counts.');
      }
      
      const response = await axios.post(`${API}/quiz/generate`, {
        subject: quizSubject,
        difficulty: quizDifficulty,
        number_of_questions: numberOfQuestions,
        time_limit: selectedTimeLimit
      }, {
        timeout: 120000 // 2 minute timeout for large question generation
      });
      
      setCurrentQuiz(response.data.questions);
      setQuizData(response.data); // Store full quiz data including curriculum
      setQuizAnswers({});
      
      // Set timer from user selection
      setTimeLimit(selectedTimeLimit);
      setTimeRemaining(selectedTimeLimit);
      setTimerActive(true);
      setQuizStartTime(Date.now());
      
      const minutes = Math.floor(selectedTimeLimit / 60);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      let timeMessage = '';
      if (hours > 0) {
        timeMessage = `${hours}h ${remainingMinutes}m`;
      } else {
        timeMessage = `${minutes} minutes`;
      }
      
      const actualQuestions = response.data.questions.length;
      const successMessage = actualQuestions === numberOfQuestions 
        ? `Quiz generated! ${numberOfQuestions} questions | Time: ${timeMessage}`
        : `Quiz generated! ${actualQuestions} questions (building up to ${numberOfQuestions}) | Time: ${timeMessage}`;
      
      toast.success(successMessage);
    } catch (error) {
      let errorMessage = 'Failed to generate quiz';
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Quiz generation timed out. Try a smaller number of questions (20-30) or try again.';
      } else if (error.response?.data?.detail) {
        // Handle both string and array error formats
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!currentQuiz) return;

    // Stop timer
    setTimerActive(false);
    
    // Calculate time taken
    const timeTaken = quizStartTime ? Math.floor((Date.now() - quizStartTime) / 1000) : timeLimit - timeRemaining;

    // Show report modal instead of just toast
    setShowReportModal(true);
  };

  const handleEmotionalCheckin = async () => {
    if (!mood) {
      toast.error('Please select your mood');
      return;
    }

    try {
      await axios.post(`${API}/emotional-checkin`, {
        mood,
        energy_level: energyLevel,
        notes: ''
      });
      toast.success('Check-in recorded!');
      setMood('');
    } catch (error) {
      toast.error('Failed to record check-in');
    }
  };

  const calculateAverageScore = () => {
    if (progress.length === 0) return 0;
    const total = progress.reduce((sum, p) => sum + p.score, 0);
    return (total / progress.length).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <Card className="mb-6 p-4 bg-white shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">🎓</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Buddy AI</h1>
                <p className="text-sm text-gray-600">
                  Welcome, {user?.name}! 
                  {userProfile?.grade && <span className="ml-2 text-teal-600 font-medium">Grade {userProfile.grade}</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell onNavigate={(tab) => setActiveTab(tab)} />
              <Button onClick={onLogout} variant="outline" className="border-gray-300">
                Logout
              </Button>
            </div>
          </div>
        </Card>

        {/* Main Layout: Content + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 min-w-0">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="p-5 bg-white shadow-sm">
                <p className="text-sm text-gray-500 mb-1">Average Score</p>
                <p className="text-3xl font-bold text-teal-600">{calculateAverageScore()}%</p>
              </Card>
              <Card className="p-5 bg-white shadow-sm">
                <p className="text-sm text-gray-500 mb-1">Quizzes Taken</p>
                <p className="text-3xl font-bold text-cyan-600">{progress.length}</p>
              </Card>
              <Card className="p-5 bg-white shadow-sm">
                <p className="text-sm text-gray-500 mb-1">Achievements</p>
                <p className="text-3xl font-bold text-blue-600">{achievements.length}</p>
              </Card>
            </div>

        <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0 overflow-x-auto flex-nowrap" style={{ scrollbarWidth: 'thin' }}>
              <TabsTrigger
                value="chat"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                data-testid="chat-tab"
              >
                Chat with Buddy
              </TabsTrigger>
              <TabsTrigger
                value="quiz"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                data-testid="quiz-tab"
              >
                Practice Quiz
              </TabsTrigger>
              <TabsTrigger
                value="checkin"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                data-testid="checkin-tab"
              >
                Check-in
              </TabsTrigger>
              <TabsTrigger
                value="reports"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                data-testid="reports-tab"
              >
                Reports
              </TabsTrigger>
              {studentMappings.has_teacher && (
                <>
                  <TabsTrigger
                    value="progress"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                    data-testid="progress-tab"
                  >
                    Progress
                  </TabsTrigger>
                  <TabsTrigger
                    value="assignments"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                  >
                    Assignments
                  </TabsTrigger>
                  <TabsTrigger
                    value="exams"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                  >
                    Exams
                  </TabsTrigger>
                </>
              )}
              {studentMappings.has_counselor && (
                <>
                  <TabsTrigger
                    value="counselor-tasks"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                    data-testid="counselor-tasks-tab"
                  >
                    <ClipboardList className="w-3.5 h-3.5 mr-1" />
                    Tasks
                  </TabsTrigger>
                  <TabsTrigger
                    value="counselor-feedback"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent whitespace-nowrap text-sm"
                    data-testid="student-feedback-tab"
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-1" />
                    Feedback {feedbackUnread > 0 && <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-red-500 rounded-full" data-testid="feedback-unread-badge">{feedbackUnread}</span>}
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="chat" className="p-6" data-testid="chat-content">
              <div className="h-[500px] flex flex-col">
                <div className="flex-1 overflow-y-auto mb-4 space-y-4">
                  {chatHistory.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Start a conversation with Buddy!</h3>
                      <p className="text-gray-600">Ask me anything about your studies, homework, or just chat!</p>
                    </div>
                  )}
                  {chatHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`chat-message-${idx}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-2xl px-4 py-3">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message here..."
                    className="flex-1"
                    data-testid="chat-input"
                    disabled={loading}
                  />
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                    disabled={loading || !message.trim()}
                    data-testid="chat-send-button"
                  >
                    Send
                  </Button>
                </form>
              </div>
            </TabsContent>

            <TabsContent value="quiz" className="p-6" data-testid="quiz-content">
              {!currentQuiz ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Generate a Practice Quiz</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          value={quizSubject}
                          onChange={(e) => setQuizSubject(e.target.value)}
                          data-testid="quiz-subject-select"
                        >
                          <option value="mathematics">Mathematics</option>
                          <option value="science">Science</option>
                          <option value="english">English</option>
                          <option value="history">History</option>
                          <option value="geography">Geography</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          value={quizDifficulty}
                          onChange={(e) => setQuizDifficulty(e.target.value)}
                          data-testid="quiz-difficulty-select"
                        >
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Number of Questions</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          value={numberOfQuestions}
                          onChange={(e) => setNumberOfQuestions(parseInt(e.target.value))}
                          data-testid="quiz-number-select"
                        >
                          <option value={5}>5 Questions</option>
                          <option value={10}>10 Questions</option>
                          <option value={15}>15 Questions</option>
                          <option value={20}>20 Questions</option>
                          <option value={30}>30 Questions</option>
                          <option value={45}>45 Questions</option>
                          <option value={60}>60 Questions</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Time Limit</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          value={selectedTimeLimit}
                          onChange={(e) => setSelectedTimeLimit(parseInt(e.target.value))}
                          data-testid="quiz-time-select"
                        >
                          <option value={300}>5 Minutes</option>
                          <option value={600}>10 Minutes</option>
                          <option value={900}>15 Minutes</option>
                          <option value={1200}>20 Minutes</option>
                          <option value={1800}>30 Minutes</option>
                          <option value={2700}>45 Minutes</option>
                          <option value={3600}>1 Hour</option>
                          <option value={5400}>1.5 Hours</option>
                          <option value={7200}>2 Hours</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={handleGenerateQuiz}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                    disabled={loading}
                    data-testid="generate-quiz-button"
                  >
                    {loading ? 'Generating Quiz...' : 'Generate Quiz'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg font-semibold">{quizSubject.charAt(0).toUpperCase() + quizSubject.slice(1)} Quiz</h3>
                      <Badge className="bg-teal-100 text-teal-700 mt-1">{quizDifficulty}</Badge>
                    </div>
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${timeRemaining < 60 ? 'text-red-600 animate-pulse' : 'text-teal-600'}`} data-testid="quiz-timer">
                        {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                      </div>
                      <p className="text-xs text-gray-500">Time Remaining</p>
                    </div>
                  </div>
                  {currentQuiz.map((question, idx) => (
                    <Card key={idx} className="p-4 border border-gray-200">
                      <p className="font-medium mb-3">{idx + 1}. {question.question}</p>
                      <div className="space-y-2">
                        {question.options.map((option, optIdx) => (
                          <label
                            key={optIdx}
                            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <input
                              type="radio"
                              name={`question-${idx}`}
                              value={option.charAt(0)}
                              checked={quizAnswers[idx] === option.charAt(0)}
                              onChange={(e) => setQuizAnswers({...quizAnswers, [idx]: e.target.value})}
                              className="w-4 h-4 text-teal-600"
                              data-testid={`quiz-option-${idx}-${optIdx}`}
                              disabled={!timerActive}
                            />
                            <span>{option}</span>
                          </label>
                        ))}
                      </div>
                    </Card>
                  ))}
                  <div className="flex gap-4">
                    <Button
                      onClick={handleSubmitQuiz}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                      data-testid="submit-quiz-button"
                    >
                      Submit Quiz
                    </Button>
                    <Button
                      onClick={() => setCurrentQuiz(null)}
                      variant="outline"
                      data-testid="cancel-quiz-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="progress" className="p-6" data-testid="progress-content">
              <ProgressAnalytics />
            </TabsContent>

            <TabsContent value="checkin" className="p-6" data-testid="checkin-content">
              <div className="max-w-md mx-auto space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold mb-2">How are you feeling today?</h3>
                  <p className="text-sm text-gray-600">Let Buddy know how you're doing</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Select your mood</label>
                  <div className="grid grid-cols-5 gap-3">
                    {[
                      { emoji: '😊', label: 'happy', color: 'teal' },
                      { emoji: '😐', label: 'neutral', color: 'gray' },
                      { emoji: '😔', label: 'sad', color: 'blue' },
                      { emoji: '😰', label: 'stressed', color: 'orange' },
                      { emoji: '😴', label: 'tired', color: 'purple' }
                    ].map((m) => (
                      <button
                        key={m.label}
                        onClick={() => setMood(m.label)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          mood === m.label
                            ? `border-${m.color}-500 bg-${m.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`mood-${m.label}`}
                      >
                        <div className="text-3xl mb-1">{m.emoji}</div>
                        <div className="text-xs text-gray-600 capitalize">{m.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Energy Level: {energyLevel}/10
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={energyLevel}
                    onChange={(e) => setEnergyLevel(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    data-testid="energy-level-slider"
                  />
                </div>

                <Button
                  onClick={handleEmotionalCheckin}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                  data-testid="submit-checkin-button"
                >
                  Submit Check-in
                </Button>

                {achievements.length > 0 && (
                  <div className="mt-8">
                    <h4 className="font-semibold mb-3">Your Badges</h4>
                    <div className="flex flex-wrap gap-2">
                      {achievements.map((achievement, idx) => (
                        <Badge key={idx} className="bg-amber-100 text-amber-700 px-3 py-1">
                          🏆 {achievement.badge_type.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="reports" className="p-6" data-testid="reports-content">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-6">Quiz History & Reports</h3>
                
                {quizReports.length === 0 ? (
                  <Card className="p-8 text-center">
                    <p className="text-gray-600">No quiz reports yet. Take a quiz to see your reports here!</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {quizReports.map((report) => (
                      <Card key={report.id} className="p-4 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-lg">{report.subject.charAt(0).toUpperCase() + report.subject.slice(1)}</h4>
                              <Badge className={(report.score_percentage || 0) >= 80 ? 'bg-green-500' : (report.score_percentage || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'}>
                                {(report.score_percentage || 0).toFixed(1)}%
                              </Badge>
                              <Badge variant="outline">{report.difficulty}</Badge>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>Questions: {report.correct_answers || 0}/{report.total_questions || 0} correct</p>
                              <p>Time: {Math.floor(report.time_taken_seconds / 60)}m {report.time_taken_seconds % 60}s</p>
                              <p>Date: {new Date(report.timestamp).toLocaleDateString()} at {new Date(report.timestamp).toLocaleTimeString()}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/quiz/report/${report.id}`);
                                  // Show report in modal or new view
                                  // For now, navigate to a detailed view
                                  toast.info('Detailed report view coming soon!');
                                } catch (error) {
                                  toast.error('Failed to load report');
                                }
                              }}
                            >
                              View Details
                            </Button>
                            <Button
                              onClick={async () => {
                                try {
                                  const response = await axios.get(`${API}/quiz/report/${report.id}/pdf`, {
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `quiz_report_${report.id}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  toast.success('Report downloaded!');
                                } catch (error) {
                                  toast.error('Failed to download report');
                                }
                              }}
                            >
                              Download PDF
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="assignments" className="p-6">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-6">My Assignments</h3>
                
                {assignments.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-gray-600">No assignments assigned yet</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {assignments.map((assignment) => (
                      <Card key={assignment.id} className="p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-lg">{assignment.title}</h4>
                              {assignment.submitted ? (
                                <Badge className="bg-green-500">Submitted</Badge>
                              ) : (
                                <Badge className="bg-yellow-500">Pending</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{assignment.description}</p>
                            <div className="text-sm text-gray-500 space-y-1">
                              <p><strong>Subject:</strong> {assignment.subject}</p>
                              <p><strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleDateString()}</p>
                            </div>
                            
                            {assignment.submission && assignment.submission.grade && (
                              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                                <div className="flex gap-4 mb-2">
                                  <p className="text-sm font-medium text-green-900">
                                    Score: {assignment.submission.grade}/100
                                  </p>
                                  {assignment.submission.rating && (
                                    <Badge className="bg-purple-500">
                                      {assignment.submission.rating}
                                    </Badge>
                                  )}
                                </div>
                                {assignment.submission.feedback && (
                                  <>
                                    <p className="text-sm font-medium text-green-900 mt-2">Teacher Feedback:</p>
                                    <p className="text-sm text-gray-700">{assignment.submission.feedback}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {new Date(assignment.submission.feedback_date).toLocaleString()}
                                    </p>
                                  </>
                                )}
                              </div>
                            )}

                            {/* Question paper auto-score display */}
                            {assignment.submitted && assignment.submission?.submission_type === 'question_paper' && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Badge variant="outline" className="text-xs border-blue-300 text-blue-700" data-testid={`auto-score-${assignment.id}`}>
                                  Auto: {assignment.submission.earned_marks ?? 0}/{assignment.submission.total_marks || 0} ({assignment.submission.score_percentage ?? 0}%)
                                </Badge>
                                {assignment.submission.ai_score_percentage !== undefined && (
                                  <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
                                    AI Eval: {assignment.submission.ai_score_percentage}%
                                  </Badge>
                                )}
                                {assignment.submission.teacher_reviewed ? (
                                  <Badge className="bg-green-600 text-white text-xs">Teacher Reviewed</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">Awaiting Teacher Review</Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              data-testid={`view-assignment-${assignment.id}`}
                              onClick={() => {
                                setViewingAssignment(assignment);
                                setShowAssignmentViewDialog(true);
                              }}
                            >
                              View Details
                            </Button>
                            {!assignment.submitted && assignment.question_paper && (
                              <Button
                                data-testid={`answer-paper-${assignment.id}`}
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => openQuestionPaper(assignment)}
                              >
                                Answer Questions
                              </Button>
                            )}
                            {!assignment.submitted && !assignment.question_paper && (
                              <Button
                                data-testid={`submit-assignment-${assignment.id}`}
                                onClick={() => {
                                  setSelectedAssignment(assignment);
                                  setShowAssignmentModal(true);
                                }}
                              >
                                Submit Assignment
                              </Button>
                            )}
                            {assignment.submitted && assignment.question_paper && (
                              <Button
                                variant="outline"
                                className="border-green-300 text-green-700"
                                data-testid={`review-answers-${assignment.id}`}
                                onClick={() => {
                                  setQuestionPaperAssignment(assignment);
                                  setPaperAnswers({});
                                  setPaperResult(null);
                                  setShowQuestionPaperDialog(true);
                                }}
                              >
                                Review Answers
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="exams" className="p-6">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-6">My Exams & Tests</h3>
                
                {exams.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-gray-600">No exams assigned yet</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {exams.map((exam) => (
                      <Card key={exam.id} className="p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-lg">{exam.title}</h4>
                              {exam.submitted ? (
                                <Badge className="bg-green-500">Completed</Badge>
                              ) : (
                                <Badge className="bg-blue-500">Available</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{exam.description}</p>
                            <div className="text-sm text-gray-500 space-y-1">
                              <p><strong>Subject:</strong> {exam.subject}</p>
                              <p><strong>Questions:</strong> {exam.questions?.length || 0}</p>
                              <p><strong>Time Limit:</strong> {exam.time_limit_minutes} minutes</p>
                              <p><strong>Total Points:</strong> {exam.total_points}</p>
                              <p><strong>Due Date:</strong> {new Date(exam.due_date).toLocaleDateString()}</p>
                            </div>
                            {exam.submitted && exam.submission && (
                              <div className="mt-3 p-3 bg-green-50 rounded">
                                <p className="text-sm font-semibold text-green-900">
                                  Score: {exam.submission.total_score ?? 0}/{exam.total_points || 0} ({(exam.submission.percentage ?? 0).toFixed(1)}%)
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {!exam.submitted ? (
                              <Button
                                onClick={() => handleStartExam(exam.id)}
                                disabled={loading}
                              >
                                Start Exam
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setExamSubmissionResult(exam.submission);
                                  setShowExamReportModal(true);
                                }}
                              >
                                View Report
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="counselor-tasks" className="p-6" data-testid="counselor-tasks-content">
              <CounselorTasks />
            </TabsContent>

            <TabsContent value="counselor-feedback" className="p-6" data-testid="counselor-feedback-content">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-teal-600" /> Counselor Feedback</h3>
              <CounselorFeedbackList
                feedback={counselorFeedback}
                currentUserId={user?.id}
                onMarkRead={markFeedbackRead}
              />
            </TabsContent>
          </Tabs>
        </Card>
          </div>

          {/* Gamification Sidebar */}
          <div className="lg:col-span-1" data-testid="gamification-sidebar">
            <div className="sticky top-24">
              <GamificationPanel />
            </div>
          </div>
        </div>

        {/* Quiz Report Modal */}
        {showReportModal && currentQuiz && (
          <QuizReportModal
            quizData={quizData}
            quizAnswers={quizAnswers}
            currentQuiz={currentQuiz}
            subject={quizSubject}
            difficulty={quizDifficulty}
            gradeLevel={userProfile?.grade || user?.grade || '10th'}
            timeTaken={quizStartTime ? Math.floor((Date.now() - quizStartTime) / 1000) : timeLimit - timeRemaining}
            onClose={handleReportModalClose}
            onSaveComplete={handleReportModalClose}
          />
        )}

        {/* Exam Taking Modal */}
        <ExamTakingModal
          exam={selectedExam}
          open={showExamModal}
          onClose={() => setShowExamModal(false)}
          onSubmit={handleExamSubmit}
        />

        {/* Exam Report Modal */}
        <ExamReportModal
          submission={examSubmissionResult}
          open={showExamReportModal}
          onClose={() => setShowExamReportModal(false)}
        />

        {/* Assignment View Details Dialog (Readonly) */}
        <Dialog open={showAssignmentViewDialog} onOpenChange={setShowAssignmentViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Assignment Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{viewingAssignment?.title}</h3>
                <div className="flex gap-2 mb-3">
                  {viewingAssignment?.submitted ? (
                    <Badge className="bg-green-500">Submitted</Badge>
                  ) : (
                    <Badge className="bg-yellow-500">Pending</Badge>
                  )}
                </div>
              </div>
              
              <div>
                <Label className="text-base font-semibold">Description</Label>
                <div className="mt-2 p-4 bg-gray-50 rounded border border-gray-200">
                  <p className="text-gray-700 whitespace-pre-wrap">{viewingAssignment?.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-base font-semibold">Subject</Label>
                  <p className="mt-1 text-gray-700">{viewingAssignment?.subject}</p>
                </div>
                <div>
                  <Label className="text-base font-semibold">Grade Level</Label>
                  <p className="mt-1 text-gray-700">{viewingAssignment?.grade_level}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-base font-semibold">Due Date</Label>
                <p className="mt-1 text-gray-700">
                  {viewingAssignment && new Date(viewingAssignment.due_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              
              {viewingAssignment?.submitted && (
                <div className="p-4 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm font-medium text-green-900">
                    ✓ You have submitted this assignment
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setShowAssignmentViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Submission Modal */}
        <Dialog open={showAssignmentModal} onOpenChange={setShowAssignmentModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Submit Assignment: {selectedAssignment?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="assignment-file">Upload Word Document (.doc or .docx)</Label>
                <Input
                  id="assignment-file"
                  type="file"
                  accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleAssignmentFileChange}
                  className="mt-2"
                />
                {assignmentFile && (
                  <p className="text-sm text-green-600 mt-2">
                    Selected: {assignmentFile.name} ({(assignmentFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="assignment-notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="assignment-notes"
                  value={assignmentNotes}
                  onChange={(e) => setAssignmentNotes(e.target.value)}
                  placeholder="Add any additional comments or notes about your submission..."
                  rows={4}
                  className="mt-2"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-800">
                  <strong>Assignment Details:</strong>
                </p>
                <p className="text-sm text-blue-700 mt-1">{selectedAssignment?.description}</p>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>Due Date:</strong> {selectedAssignment && new Date(selectedAssignment.due_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignmentModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignmentSubmit} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Assignment'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Question Paper Answer Dialog */}
        <Dialog open={showQuestionPaperDialog} onOpenChange={(open) => { if (!open) { setShowQuestionPaperDialog(false); setQuestionPaperAssignment(null); setPaperResult(null); } }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {questionPaperAssignment?.title || 'Question Paper'}
              </DialogTitle>
            </DialogHeader>

            {questionPaperAssignment?.question_paper && (
              <div className="space-y-4">
                {/* If submitted, show read-only with results */}
                {questionPaperAssignment.submitted ? (
                  <>
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Submitted</span>
                      </div>
                      {questionPaperAssignment.submission?.score_percentage !== undefined && (
                        <Badge className="bg-blue-600 text-white">
                          Score: {questionPaperAssignment.submission.earned_marks ?? 0}/{questionPaperAssignment.submission.total_marks || 0} ({questionPaperAssignment.submission.score_percentage ?? 0}%)
                        </Badge>
                      )}
                    </div>
                    <QuestionPaperView
                      questionPaper={questionPaperAssignment.question_paper}
                      answers={{}}
                      onAnswerChange={() => {}}
                      readOnly={true}
                      submission={questionPaperAssignment.submission}
                    />
                  </>
                ) : (
                  <>
                    {/* Score result after submission */}
                    {paperResult && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                        <p className="text-lg font-bold text-blue-900">
                          Score: {paperResult.earned_marks ?? 0}/{paperResult.total_marks || 0}
                        </p>
                        <p className="text-2xl font-bold text-blue-700">{paperResult.score_percentage ?? 0}%</p>
                        <p className="text-sm text-gray-500 mt-1">Your answers have been submitted for review</p>
                      </div>
                    )}

                    {/* Interactive question paper */}
                    {!paperResult && (
                      <>
                        <QuestionPaperView
                          questionPaper={questionPaperAssignment.question_paper}
                          answers={paperAnswers}
                          onAnswerChange={(key, value) => setPaperAnswers(prev => ({ ...prev, [key]: value }))}
                          readOnly={false}
                        />
                        <div className="sticky bottom-0 bg-white pt-3 border-t flex items-center justify-between">
                          <p className="text-sm text-gray-500">
                            Answered: {Object.values(paperAnswers).filter(a => a !== '' && a !== null && a !== undefined && (!Array.isArray(a) || a.length > 0)).length} / {
                              (questionPaperAssignment.question_paper.sections || []).reduce((a, s) => a + (s.questions?.length || 0), 0)
                            }
                          </p>
                          <Button
                            data-testid="submit-paper-answers-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 px-6"
                            onClick={handleQuestionPaperSubmit}
                            disabled={submittingPaper}
                          >
                            {submittingPaper ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            {submittingPaper ? 'Submitting...' : 'Submit Answers'}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default StudentDashboard;
